const RELOAD_KEY_PREFIX = 'rs:stale-dynamic-import:';
const RELOAD_COOLDOWN_MS = 60_000;

const DYNAMIC_IMPORT_FAILURES = [
  /failed to fetch dynamically imported module/i,
  /importing a module script failed/i,
  /loading chunk .+ failed/i,
  /chunkloaderror/i,
];

type RecoveryEnvironment = {
  now: () => number;
  route: () => string;
  storage: Pick<Storage, 'getItem' | 'setItem'>;
  reload: () => void;
};

function errorMessage(reason: unknown): string {
  if (reason instanceof Error) return `${reason.name}: ${reason.message}`;
  return String(reason ?? '');
}

export function isStaleDynamicImportFailure(reason: unknown): boolean {
  const message = errorMessage(reason);
  return DYNAMIC_IMPORT_FAILURES.some((pattern) => pattern.test(message));
}

/**
 * Un despliegue reemplaza los archivos con hash de Vite. Una pestaña abierta
 * puede conservar el index anterior e intentar cargar un módulo que ya no
 * existe. En ese caso actualizamos la aplicación una sola vez por ruta. El
 * intervalo evita un bucle si el problema real no es un despliegue reciente.
 */
export function recoverFromStaleDynamicImport(
  reason: unknown,
  environment?: RecoveryEnvironment,
): boolean {
  if (!isStaleDynamicImportFailure(reason)) return false;

  const currentEnvironment: RecoveryEnvironment = environment ?? {
    now: Date.now,
    route: () => `${window.location.pathname}${window.location.search}`,
    storage: window.sessionStorage,
    reload: () => window.location.reload(),
  };
  const key = `${RELOAD_KEY_PREFIX}${currentEnvironment.route()}`;
  const now = currentEnvironment.now();

  try {
    const lastReload = Number(currentEnvironment.storage.getItem(key));
    if (lastReload > 0 && now - lastReload < RELOAD_COOLDOWN_MS) return false;
    currentEnvironment.storage.setItem(key, String(now));
  } catch {
    // Sin almacenamiento no podemos garantizar que la recarga no se repita.
    return false;
  }

  currentEnvironment.reload();
  return true;
}
